'use client'

import { ExternalLink, LineChart, LinkIcon, OctagonAlert } from 'lucide-react'

import { HideTokenDialogContent } from '~/components/smart-accounts/HideTokenDialogContent'
import { TokenPriceChartDialog } from '~/components/smart-accounts/TokenPriceChartDialog'
import { SupportedChainId } from '~/constants/chains'
import { useAuth } from '~/context/AuthProvider'
import { IAddress } from '~/types'
import {
  getExplorerAddressLinkChainId,
  getExplorerAddressLinkToken,
} from '~/utils/addresses'
import { copyToClipboard } from '~/utils/copyToClipboard'
import { isNativeTokenAddress } from '~/utils/tokens'
import { sendTrackingData } from '~/utils/tracking'

import type { DropdownEntry } from '~/components/component-library/Dropdown'

type SetGlobalModalState = ReturnType<typeof useAuth>['setGlobalModalState']

// Token actions that don't require a smart account, shared by the smart
// account balances rows and the card assets rows
export const buildTokenInfoDropdownItems = ({
  token,
  chainId,
  holderAddress,
  smartAccountAddress,
  setGlobalModalState,
  closeGlobalModal,
}: {
  token: IAddress
  chainId: SupportedChainId
  // Whose explorer token page to open. Omit when there's no single holder —
  // then falls back to the token's contract page.
  holderAddress?: IAddress
  // Forwarded to the hide dialog so it can patch that account's cached balances
  smartAccountAddress?: IAddress
  setGlobalModalState: SetGlobalModalState
  closeGlobalModal: () => void
}): DropdownEntry[] => {
  const explorerPath = holderAddress
    ? getExplorerAddressLinkToken(holderAddress, chainId, token)
    : getExplorerAddressLinkChainId(token, chainId)
  const isNativeToken = isNativeTokenAddress(token)

  return [
    {
      content: 'Copy address',
      icon: <LinkIcon />,
      onClick: () =>
        copyToClipboard(token, 'Token address copied to clipboard'),
    },
    {
      content: 'Open in explorer',
      icon: <ExternalLink />,
      href: explorerPath ? { path: explorerPath, newTab: true } : undefined,
    },
    {
      content: 'View price chart',
      icon: <LineChart />,
      onClick: () => {
        sendTrackingData('viewedPriceChart', { token, chainId })
        setGlobalModalState({
          isOpen: true,
          content: <TokenPriceChartDialog token={token} chainId={chainId} />,
        })
      },
    },
    'divider',
    {
      content: 'Report spam...',
      icon: <OctagonAlert />,
      disabled: isNativeToken,
      tooltip: isNativeToken
        ? 'Unable to report native token as spam'
        : undefined,
      onClick: () =>
        setGlobalModalState({
          isOpen: true,
          content: (
            <HideTokenDialogContent
              smartAccountAddress={smartAccountAddress}
              setIsOpen={closeGlobalModal}
              token={token}
              chainId={chainId}
              assetType="ERC20"
            />
          ),
        }),
    },
  ]
}
